import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useSwipeable } from 'react-swipeable';
import TaskCheckbox from '../../../features/tasks/components/TaskRow/TaskCheckbox/TaskCheckbox';
import ProgressIndicator from '../../../features/tasks/components/TaskRow/ProgressIndicator/ProgressIndicator';
import ExpandButton from '../../../features/tasks/components/TaskRow/ExpandButton/ExpandButton';
import { getSubtaskStats } from '../../../features/tasks/utils/taskUtils';
import './FeatureShowcase.css';

const MOBILE_BREAKPOINT = 768;

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT}px)`).matches : false
  );
  useEffect(() => {
    const mql = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT}px)`);
    const handler = () => setIsMobile(mql.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, []);
  return isMobile;
}

const TABS = [
  { id: 'subtasks', label: 'Subtasks' },
  { id: 'sequential', label: 'Sequential tasks' },
  { id: 'custom-status', label: 'Custom status' },
  { id: 'due-dates', label: 'Due dates' },
  { id: 'import-tasks', label: 'Import tasks' }
];

/** Kept for potential future use (e.g. tooltips); description UI was removed */
const TAB_DESCRIPTIONS = {
  subtasks: 'Break work into smaller steps. Parent tasks show progress',
  sequential: 'Mark tasks as sequential when they must be completed in order. Later tasks stay disabled until earlier ones are complete.',
  'custom-status': "Use custom statuses: Work in progress, Waiting, or Won't do. Long-press the checkbox to select custom status.",
  'due-dates': 'Add due dates to tasks. Subtasks inherit the parent\'s date.',
  'import-tasks': 'Paste a list from anywhere - one task per line - and import it into your list.'
};

const noop = () => {};

/** Renders a keycap-style element for shortcut hints. */
function Key({ children }) {
  return <span className="feature-showcase-key">{children}</span>;
}

/** Build a minimal task shape for showcase (id, _reactKey, status, text, subtasks, order). */
function makeShowcaseTask(overrides = {}) {
  const { id, _reactKey, ...rest } = overrides;
  const key = _reactKey ?? id ?? `showcase-${Math.random().toString(36).slice(2, 9)}`;
  return {
    id: id ?? key,
    _reactKey: key,
    status: 'incomplete',
    text: '',
    order: 0,
    subtasks: [],
    ...rest
  };
}

const isTaskComplete = (task) => task.status === 'complete' || task.status === 'wont_do';

/** Example row that uses real TaskCheckbox (and optionally ProgressIndicator, ExpandButton). */
function ExampleRow({ task, tasks = [], children, className = '', showExpand = false, isExpanded = false, showProgress = false, isSequentialDisabled = false }) {
  const subtaskStats = showProgress && task.subtasks?.length ? getSubtaskStats(task) : null;
  const complete = isTaskComplete(task);
  return (
    <li className={`feature-showcase-example-row ${className} ${complete ? 'feature-showcase-row-complete' : ''}`}>
      <div className="feature-showcase-example-content">
        {showExpand && (
          <span onClick={noop} className="expand-button-desktop" style={{ display: 'inline-block' }}>
            <ExpandButton hasSubtasks={true} isExpanded={isExpanded} onToggleExpand={noop} />
          </span>
        )}
        {showProgress && subtaskStats && subtaskStats.total > 0 ? (
          <ProgressIndicator
            task={task}
            tasks={tasks}
            subtaskStats={subtaskStats}
            isSequentialDisabled={false}
            isBlockingTaskHighlighted={false}
            isBlockingTaskNotBlocked={false}
            onBlockedIconMouseEnter={noop}
            onBlockedIconMouseLeave={noop}
          />
        ) : (
          <TaskCheckbox
            task={task}
            isSequential={isSequentialDisabled}
            isSequentialDisabled={isSequentialDisabled}
            onCheckboxChange={noop}
            onBlockedIconMouseEnter={noop}
            onBlockedIconMouseLeave={noop}
            disableLongPress
          />
        )}
        {children}
      </div>
    </li>
  );
}

function SubtasksExample({ isMobile = false }) {
  const sub1 = makeShowcaseTask({ id: 'sub-1', _reactKey: 'sub-1', status: 'complete', text: 'Buy milk', order: 1 });
  const sub2 = makeShowcaseTask({ id: 'sub-2', _reactKey: 'sub-2', status: 'complete', text: 'Post parcel', order: 2 });
  const sub3 = makeShowcaseTask({ id: 'sub-3', _reactKey: 'sub-3', status: 'incomplete', text: 'Pick up prescription', order: 3 });
  const parent = makeShowcaseTask({
    id: 'parent-1',
    _reactKey: 'parent-1',
    text: 'My errands',
    order: 0,
    subtasks: [sub1, sub2, sub3]
  });
  const allTasks = [parent, sub1, sub2, sub3];

  return (
    <div className="feature-showcase-panel-content feature-showcase-panel-content-with-explanation">
      <p className="feature-showcase-explanation">
        Break work into steps with subtasks; the parent shows progress and completes when all are done. <Key>Enter</Key> adds a task, <Key>{isMobile ? 'Space' : 'Tab'}</Key> makes it a subtask of the one above.
      </p>
      <ul className="feature-showcase-example-list">
        <ExampleRow task={parent} tasks={allTasks} showExpand showProgress isExpanded>
          <span className="feature-showcase-text">My errands</span>
        </ExampleRow>
        <ul className="feature-showcase-sublist">
          <ExampleRow task={sub1} className="feature-showcase-subtask">
            <span className="feature-showcase-text feature-showcase-text-done">Buy milk</span>
          </ExampleRow>
          <ExampleRow task={sub2} className="feature-showcase-subtask">
            <span className="feature-showcase-text feature-showcase-text-done">Post parcel</span>
          </ExampleRow>
          <ExampleRow task={sub3} className="feature-showcase-subtask">
            <span className="feature-showcase-text">Pick up prescription</span>
          </ExampleRow>
        </ul>
      </ul>
    </div>
  );
}

function SequentialExample() {
  const t1 = makeShowcaseTask({ id: 'seq-1', _reactKey: 'seq-1', status: 'complete', text: '1. Choose recipe', order: 1 });
  const t2 = makeShowcaseTask({ id: 'seq-2', _reactKey: 'seq-2', status: 'incomplete', text: '2. Check pantry', order: 2 });
  const t3 = makeShowcaseTask({ id: 'seq-3', _reactKey: 'seq-3', status: 'incomplete', text: '3. Buy ingredients', order: 3 });
  const t4 = makeShowcaseTask({ id: 'seq-4', _reactKey: 'seq-4', status: 'incomplete', text: '4. Cook and serve', order: 4 });

  return (
    <div className="feature-showcase-panel-content feature-showcase-panel-content-with-explanation">
      <p className="feature-showcase-explanation">
        Mark tasks as sequential so they must be done in order. Later tasks stay disabled until earlier ones are complete.
      </p>
      <ul className="feature-showcase-example-list">
        <ExampleRow task={t1}>
          <span className="feature-showcase-text feature-showcase-text-done">1. Choose recipe</span>
        </ExampleRow>
        <ExampleRow task={t2}>
          <span className="feature-showcase-text">2. Check pantry</span>
        </ExampleRow>
        <ExampleRow task={t3}isSequentialDisabled className="feature-showcase-sequential-blocked">
          <span className="feature-showcase-text">3. Buy ingredients</span>
        </ExampleRow>
        <ExampleRow task={t4} isSequentialDisabled className="feature-showcase-sequential-blocked">
          <span className="feature-showcase-text">4. Cook and serve</span>
        </ExampleRow>
      </ul>
    </div>
  );
}

function CustomStatusExample() {
  const done = makeShowcaseTask({ id: 'cs-1', _reactKey: 'cs-1', status: 'complete', text: 'Done', order: 1 });
  const wip = makeShowcaseTask({ id: 'cs-2', _reactKey: 'cs-2', status: 'wip', text: 'In progress', order: 2 });
  const waiting = makeShowcaseTask({ id: 'cs-3', _reactKey: 'cs-3', status: 'waiting', text: 'Waiting', order: 3 });
  const wontDo = makeShowcaseTask({ id: 'cs-4', _reactKey: 'cs-4', status: 'wont_do', text: "Won't do", order: 4 });


  return (
    <div className="feature-showcase-panel-content feature-showcase-panel-content-with-explanation">
      <p className="feature-showcase-explanation">
        Use In progress, Waiting, or Won&apos;t do. Long-press the circle to open the status menu.
      </p>
      <ul className="feature-showcase-example-list">
        <ExampleRow task={done}>
          <span className="feature-showcase-text feature-showcase-text-done">Done</span>
        </ExampleRow>
        <ExampleRow task={wip}>
          <span className="feature-showcase-text">In progress</span>
        </ExampleRow>
        <ExampleRow task={waiting}>
          <span className="feature-showcase-text">Waiting</span>
        </ExampleRow>
        <ExampleRow task={wontDo}>
          <span className="feature-showcase-text feature-showcase-text-done">Won&apos;t do</span>
        </ExampleRow>
      </ul>
    </div>
  );
}

function DueDatesExample() {
  const fridayParent = makeShowcaseTask({
    id: 'dd-parent',
    _reactKey: 'dd-parent',
    status: 'incomplete',
    text: 'Friday',
    order: 0,
    subtasks: []
  });
  const fridaySub = makeShowcaseTask({
    id: 'dd-sub',
    _reactKey: 'dd-sub',
    status: 'incomplete',
    text: 'Reply to John',
    order: 1,
    parent_task_id: 'dd-parent'
  });
  fridayParent.subtasks = [fridaySub];
  const allDue = [fridayParent, fridaySub];
  const today = makeShowcaseTask({ id: 'dd-1', _reactKey: 'dd-1', status: 'incomplete', text: 'Call dentist', order: 2 });
  const over = makeShowcaseTask({ id: 'dd-2', _reactKey: 'dd-2', status: 'incomplete', text: 'Meal plan', order: 3 });

  return (
    <div className="feature-showcase-panel-content feature-showcase-panel-content-with-explanation feature-showcase-panel-content-due">
      <p className="feature-showcase-explanation">
        Add due dates and filter by Today or Next 7 days. Subtasks inherit the parent&apos;s date. Type a day name or date to set it; overdue tasks are highlighted.
      </p>
      <ul className="feature-showcase-example-list">
        <ExampleRow task={fridayParent} tasks={allDue} showExpand isExpanded>
          <span className="feature-showcase-text">Friday</span>
          <span className="feature-showcase-due-wrap">
            <span className="feature-showcase-due">Fri</span>
            <span className="feature-showcase-due-inherit-icon" title="Subtasks created will inherit this due date" aria-hidden="true">
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                <g transform="rotate(180 6 6)">
                  <path d="M6 2L9 6H3L6 2Z" fill="currentColor"/>
                  <path d="M6 6V10" stroke="currentColor" strokeWidth="1" strokeLinecap="round"/>
                </g>
              </svg>
            </span>
          </span>
        </ExampleRow>
        <ul className="feature-showcase-sublist feature-showcase-sublist-due">
          <ExampleRow task={fridaySub} className="feature-showcase-subtask">
            <span className="feature-showcase-text">Reply to John</span>
            <span className="feature-showcase-due">Fri</span>
          </ExampleRow>
        </ul>
        <ExampleRow task={today}>
          <span className="feature-showcase-text">Call dentist</span>
          <span className="feature-showcase-due feature-showcase-due-today">Today</span>
        </ExampleRow>
        <ExampleRow task={over}>
          <span className="feature-showcase-text">Meal plan</span>
          <span className="feature-showcase-due feature-showcase-due-overdue">Overdue</span>
        </ExampleRow>
      </ul>
    </div>
  );
}

function ImportTasksExample({ isMobile }) {
  const importTasks = [
    { id: 'imp-1', _reactKey: 'imp-1', text: 'Take a walk', order: 1 },
    { id: 'imp-2', _reactKey: 'imp-2', text: 'Call dentist', order: 2 },

  ];

  if(!isMobile) {
    importTasks.push({ id: 'imp-3', _reactKey: 'imp-3', text: 'Buy groceries', order: 3 });
  }

  const tasksToShow = isMobile ? importTasks.slice(0, 3) : importTasks;
  const pasteText = isMobile
    ? `Take a walk\nCall dentist\n`
    : `Take a walk\nCall dentist\nBuy groceries\n`;

  return (
    <div className="feature-showcase-panel-content feature-showcase-panel-content-with-explanation">
      <p className="feature-showcase-explanation">
        Paste a list (one task per line) from anywhere; it becomes tasks you can edit. Sign up to use import and keep multiple lists.
      </p>
      <div className="feature-showcase-import-demo">
        <div className="feature-showcase-import-source">
          <div className="feature-showcase-import-label">Paste your list</div>
          <pre className="feature-showcase-import-text">
            {pasteText}
          </pre>
        </div>
        <div className="feature-showcase-import-arrow" aria-hidden="true">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M5 12h14M12 5l7 7-7 7"/>
          </svg>
        </div>
        <div className="feature-showcase-import-result">
          <div className="feature-showcase-import-label">Becomes tasks</div>
          <ul className="feature-showcase-example-list feature-showcase-import-list">
            {tasksToShow.map((t) => (
              <ExampleRow key={t.id} task={makeShowcaseTask({ ...t, status: 'incomplete' })}>
                <span className="feature-showcase-text">{t.text}</span>
              </ExampleRow>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

function FeatureShowcase({ isMinimized: isMinimizedProp, onMinimizedChange, onClose }) {
  const [activeTab, setActiveTab] = useState(TABS[0].id);
  const [internalMinimized, setInternalMinimized] = useState(false);
  const isMobile = useIsMobile();
  const isControlled = onMinimizedChange != null;
  const isMinimized = isControlled ? (isMinimizedProp ?? false) : internalMinimized;

  const activeIndex = TABS.findIndex((t) => t.id === activeTab);
  const setTabByIndex = useCallback((index) => {
    setActiveTab(TABS[(index + TABS.length) % TABS.length].id);
  }, []);
  const goPrev = useCallback(() => setTabByIndex(activeIndex - 1), [activeIndex, setTabByIndex]);
  const goNext = useCallback(() => setTabByIndex(activeIndex + 1), [activeIndex, setTabByIndex]);

  const [dragOffset, setDragOffset] = useState(0);
  const [slideWidthPx, setSlideWidthPx] = useState(0);
  const [snapAnimating, setSnapAnimating] = useState(false);
  const scrollContainerRef = useRef(null);
  const snapCallbackRef = useRef(null);
  const resizeObserverRef = useRef(null);
  const swipeHandlersRef = useRef(null);

  const handleSliderTransitionEnd = useCallback((e) => {
    if (e.target !== e.currentTarget || e.propertyName !== 'transform') return;
    const cb = snapCallbackRef.current;
    snapCallbackRef.current = null;
    if (cb) cb();
  }, []);

  const getScrollContentWidth = (element) => {
    if (!element) return 0;
    const style = window.getComputedStyle(element);
    const paddingL = parseFloat(style.paddingLeft) || 0;
    const paddingR = parseFloat(style.paddingRight) || 0;
    return element.offsetWidth - paddingL - paddingR;
  };

  const getSlideWidth = () => {
    const el = scrollContainerRef.current;
    const w = slideWidthPx || (el ? getScrollContentWidth(el) : 0);
    if (w > 0 && !slideWidthPx && el) setSlideWidthPx(getScrollContentWidth(el));
    return w;
  };

  const swipeHandlers = useSwipeable({
    onSwiping: (e) => {
      if (!isMobile || snapAnimating) return;
      const width = getSlideWidth();
      if (width <= 0) return;
      let dx = e.deltaX;
      if (activeIndex === 0 && dx > 0) dx *= 0.3;
      if (activeIndex === TABS.length - 1 && dx < 0) dx *= 0.3;
      setDragOffset(dx);
    },
    onSwiped: (e) => {
      if (!isMobile || snapAnimating) return;
      const width = getSlideWidth();
      if (width <= 0) return;
      const threshold = width * 0.25;
      if (e.deltaX < -threshold && activeIndex < TABS.length - 1) {
        setSnapAnimating(true);
        setDragOffset(-width);
        snapCallbackRef.current = () => {
          goNext();
          setDragOffset(0);
          setSnapAnimating(false);
        };
      } else if (e.deltaX > threshold && activeIndex > 0) {
        setSnapAnimating(true);
        setDragOffset(width);
        snapCallbackRef.current = () => {
          goPrev();
          setDragOffset(0);
          setSnapAnimating(false);
        };
      } else {
        setSnapAnimating(true);
        setDragOffset(0);
        snapCallbackRef.current = () => setSnapAnimating(false);
      }
    },
    onTouchStartOrOnMouseDown: () => {
      if (!snapAnimating) setDragOffset(0);
    },
    trackMouse: false,
    delta: 10,
    preventScrollOnSwipe: true
  });
  swipeHandlersRef.current = swipeHandlers;
  const { ref: _swipeRef, ...swipeHandlerProps } = swipeHandlers;

  const scrollRefCallback = useCallback((el) => {
    swipeHandlersRef.current?.ref?.(el);
    scrollContainerRef.current = el;
    if (resizeObserverRef.current) {
      resizeObserverRef.current.disconnect();
      resizeObserverRef.current = null;
    }
    if (el && isMobile) {
      setSlideWidthPx(getScrollContentWidth(el));
      const ro = new ResizeObserver(() => setSlideWidthPx(getScrollContentWidth(el)));
      ro.observe(el);
      resizeObserverRef.current = ro;
    } else {
      setSlideWidthPx(0);
    }
  }, [isMobile]);

  const handleArrowPrev = useCallback(() => {
    if (isMobile && !snapAnimating && activeIndex > 0) {
      const width = getSlideWidth();
      if (width > 0) {
        setSnapAnimating(true);
        setDragOffset(width);
        snapCallbackRef.current = () => {
          goPrev();
          setDragOffset(0);
          setSnapAnimating(false);
        };
        return;
      }
    }
    goPrev();
  }, [isMobile, snapAnimating, activeIndex, goPrev]);

  const handleArrowNext = useCallback(() => {
    if (isMobile && !snapAnimating && activeIndex < TABS.length - 1) {
      const width = getSlideWidth();
      if (width > 0) {
        setSnapAnimating(true);
        setDragOffset(-width);
        snapCallbackRef.current = () => {
          goNext();
          setDragOffset(0);
          setSnapAnimating(false);
        };
        return;
      }
    }
    goNext();
  }, [isMobile, snapAnimating, activeIndex, goNext]);

  const setMinimized = (value) => {
    if (isControlled) {
      onMinimizedChange?.(value);
    } else {
      setInternalMinimized(value);
    }
  };

  const handleClose = () => {
    if (onClose) {
      onClose();
    } else {
      setMinimized(true);
    }
  };

  const handleReopen = () => {
    setMinimized(false);
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  const renderPanelContent = (tabId) => {
    switch (tabId) {
      case 'subtasks':
        return <SubtasksExample isMobile={isMobile} />;
      case 'sequential':
        return <SequentialExample />;
      case 'custom-status':
        return <CustomStatusExample />;
      case 'due-dates':
        return <DueDatesExample />;
      case 'import-tasks':
        return <ImportTasksExample isMobile={isMobile} />;
      default:
        return null;
    }
  };

  const renderPanel = () => renderPanelContent(activeTab);

  const modalContent = (
    <div className="feature-showcase-backdrop" onClick={handleBackdropClick}>
      <div className="feature-showcase-modal" onClick={(e) => e.stopPropagation()}>
        <div className="feature-showcase-toolbar">
          {isMobile ? (
            <div className="feature-showcase-mobile-nav">
              <button
                type="button"
                className="feature-showcase-arrow text-non-selectable"
                onClick={handleArrowPrev}
                aria-label="Previous feature"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M15 18l-6-6 6-6"/>
                </svg>
              </button>
              <div className="feature-showcase-mobile-dots-wrap">
                <div className="feature-showcase-dots" role="tablist" aria-label="Features">
                  {TABS.map((tab, index) => (
                    <button
                      key={tab.id}
                      type="button"
                      role="tab"
                      aria-selected={activeTab === tab.id}
                      aria-label={`${tab.label}, feature ${index + 1} of ${TABS.length}`}
                      className={`feature-showcase-dot text-non-selectable ${activeTab === tab.id ? 'active' : ''}`}
                      onClick={() => setActiveTab(tab.id)}
                    />
                  ))}
                </div>
              </div>
              <button
                type="button"
                className="feature-showcase-arrow text-non-selectable"
                onClick={handleArrowNext}
                aria-label="Next feature"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9 18l6-6-6-6"/>
                </svg>
              </button>
            </div>
          ) : (
            <div className="feature-showcase-tabs">
              {TABS.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  role="tab"
                  className={`feature-showcase-tab text-non-selectable ${activeTab === tab.id ? 'active' : ''}`}
                  onClick={() => setActiveTab(tab.id)}
                  aria-selected={activeTab === tab.id}
                  aria-controls="feature-showcase-panel"
                  id={`tab-${tab.id}`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          )}
          <button
            type="button"
            className="feature-showcase-close text-non-selectable"
            onClick={handleClose}
            aria-label={onClose ? 'Close' : 'Close help'}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M4 4L12 12M12 4L4 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </button>
        </div>
        <div
          ref={isMobile ? scrollRefCallback : undefined}
          className={`feature-showcase-scroll${isMobile ? ' feature-showcase-scroll-slider' : ''}`}
          {...(isMobile ? swipeHandlerProps : {})}
        >
          {isMobile ? (
            <div
              className="feature-showcase-slider-track"
              style={{
                transform: `translateX(calc(-${activeIndex * (100 / TABS.length)}% + ${dragOffset}px))`,
                transition: snapAnimating ? 'transform 0.28s ease-out' : 'none'
              }}
              onTransitionEnd={handleSliderTransitionEnd}
            >
              {TABS.map((tab) => (
                <div
                  key={tab.id}
                  id={tab.id === activeTab ? 'feature-showcase-panel' : undefined}
                  role="tabpanel"
                  aria-labelledby={tab.id === activeTab ? `feature-showcase-mobile-title-${tab.id}` : undefined}
                  aria-hidden={tab.id !== activeTab}
                  className="feature-showcase-panel feature-showcase-slider-panel"
                >
                  <span className="feature-showcase-mobile-title" id={`feature-showcase-mobile-title-${tab.id}`}>
                    {tab.label}
                  </span>
                  {renderPanelContent(tab.id)}
                </div>
              ))}
            </div>
          ) : (
            <div
              id="feature-showcase-panel"
              role="tabpanel"
              aria-labelledby={`tab-${activeTab}`}
              className="feature-showcase-panel"
            >
              {renderPanel()}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <>
      <div className="feature-showcase-root feature-showcase-minimized">
        <button
          type="button"
          className="feature-showcase-reopen text-non-selectable"
          onClick={handleReopen}
          aria-label="Help"
        >
          Help
        </button>
      </div>
      {!isMinimized && createPortal(modalContent, document.body)}
    </>
  );
}

export default FeatureShowcase;
